'use client';

import { useState, useEffect } from 'react';
import { Table, TableBody, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, horizontalListSortingStrategy } from '@dnd-kit/sortable';

import {
  useSortConfig,
  useSetSortConfig,
  useLoadFromLocalStorage,
  useColumnOrder,
  defaultColumns,
  useSetColumnOrder,
} from '@/stores/history-columns-store';
import { cn } from '@/lib/utils';
import {
  getColumnWidthPercent,
  getHeadClassName,
  isMultilineHeaderColumn,
  type UnifiedColumn,
} from './history-table-column-utils';
import { SortableTableHead } from './history-table-columns';
import { HistoryTableRow } from './history-table-row';
import { HistoryTablePagination } from './history-table-pagination';
import { ITEMS_PER_PAGE, isServerSortableField, useHistoryTableData } from './use-history-table-data';

export function HistoryTable() {
  const [currentPage, setCurrentPage] = useState(1);

  const sortConfig = useSortConfig();
  const setSortConfig = useSetSortConfig();
  const loadFromLocalStorage = useLoadFromLocalStorage();
  const columnOrder = useColumnOrder();
  const setColumnOrder = useSetColumnOrder();
  const { historyData, loading, totalCount, error, reload } =
    useHistoryTableData(currentPage, sortConfig);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        delay: 80,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const allColumns: UnifiedColumn[] = defaultColumns;

  const orderedColumns = (() => {
    const byId = new Map(allColumns.map((col) => [col.id, col]));
    const ordered: UnifiedColumn[] = [];
    const seen = new Set<string>();

    for (const id of columnOrder) {
      const col = byId.get(id);
      if (col) {
        ordered.push(col);
        seen.add(id);
      }
    }

    for (const col of allColumns) {
      if (!seen.has(col.id)) {
        ordered.push(col);
      }
    }

    return ordered;
  })();

  useEffect(() => {
    loadFromLocalStorage();
  }, [loadFromLocalStorage]);

  const handleSort = (field: string) => {
    const newDirection = sortConfig.field === field && sortConfig.direction === 'asc' ? 'desc' : 'asc';
    setSortConfig(field, newDirection);
    setCurrentPage(1);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const allColumnIds = orderedColumns.map((col) => col.id);
      const oldIndex = allColumnIds.indexOf(String(active.id));
      const newIndex = allColumnIds.indexOf(String(over.id));
      if (oldIndex !== -1 && newIndex !== -1) {
        const newOrder = [...allColumnIds];
        const [movedItem] = newOrder.splice(oldIndex, 1);
        newOrder.splice(newIndex, 0, movedItem);
        setColumnOrder(newOrder);
      }
    }
  };

  const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  if (loading) {
    return (
      <div className="flex min-h-0 flex-1 flex-col space-y-2">
        {[...Array(12)].map((_, i) => (
          <Skeleton key={i} className="h-10 w-full" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-0 flex-1 items-center justify-center rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-8">
        <div className="text-center">
          <p className="text-sm text-destructive">{error}</p>
          <Button variant="outline" size="sm" className="mt-3" onClick={reload}>
            Reintentar
          </Button>
        </div>
      </div>
    );
  }

  const renderSortIcon = (field: string) => {
    if (sortConfig.field !== field) {
      return <ArrowUpDown className="h-3 w-3 shrink-0" />;
    }
    return sortConfig.direction === 'asc' ? (
      <ArrowUp className="h-3 w-3 shrink-0" />
    ) : (
      <ArrowDown className="h-3 w-3 shrink-0" />
    );
  };

  return (
    <div className="flex min-h-[calc(100dvh-8.5rem)] min-w-0 flex-col gap-3">
      <div className="min-h-0 min-w-0 flex-1 overflow-y-auto overflow-x-hidden rounded-lg border [&_[data-slot=table-container]]:w-full [&_[data-slot=table-container]]:overflow-hidden">
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <Table className="w-full table-fixed text-xs">
            <colgroup>
              {orderedColumns.map((column) => (
                <col key={column.id} style={{ width: getColumnWidthPercent(column.id, orderedColumns) }} />
              ))}
            </colgroup>
            <TableHeader>
              <TableRow>
                <SortableContext items={orderedColumns.filter(col => col.sortable).map(col => col.id)} strategy={horizontalListSortingStrategy}>
                  {orderedColumns.map((column) => (
                    column.sortable ? (
                      <SortableTableHead key={column.id} column={column}>
                        {isServerSortableField(column.field) ? (
                          <Button
                            variant="ghost"
                            className="h-auto w-full min-w-0 p-0 text-xs font-semibold hover:bg-transparent"
                            onClick={() => handleSort(column.field)}
                          >
                            <span
                              className={cn(
                                'flex min-w-0 items-center justify-center gap-0.5',
                                isMultilineHeaderColumn(column.id) && 'flex-col gap-0.5',
                              )}
                            >
                              <span
                                className={cn(
                                  isMultilineHeaderColumn(column.id)
                                    ? 'whitespace-normal text-center leading-tight'
                                    : 'truncate',
                                )}
                              >
                                {column.name}
                              </span>
                              {renderSortIcon(column.field)}
                            </span>
                          </Button>
                        ) : (
                          <span className="block truncate text-xs font-semibold">{column.name}</span>
                        )}
                      </SortableTableHead>
                    ) : (
                      <TableHead key={column.id} className={getHeadClassName(column.id)}>
                        {column.name}
                      </TableHead>
                    )
                  ))}
                </SortableContext>
              </TableRow>
            </TableHeader>
            <TableBody>
              {historyData.map((item) => (
                <HistoryTableRow
                  key={item.processInstanceId}
                  item={item}
                  orderedColumns={orderedColumns}
                />
              ))}
            </TableBody>
          </Table>
        </DndContext>
      </div>
      {totalPages > 1 && (
        <HistoryTablePagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalCount={totalCount}
          onPageChange={handlePageChange}
        />
      )}
    </div>
  );
}
